/**
 * Parse a Jira-style duration string into whole minutes.
 *
 * Accepts a mixed sequence of tokens with suffixes `w`, `d`, `h`, or `m`, each preceded by a
 * non-negative decimal value. Whitespace between tokens is ignored. Returns an integer count of
 * minutes, rounded to the nearest minute.
 *
 * Defaults (configurable via `options`):
 *   - 1d = 8h (hours per working day)
 *   - 1w = 5d (working days per week)
 *
 * Examples:
 *   parseDurationToMinutes("1h 30m")  → 90
 *   parseDurationToMinutes("90m")     → 90
 *   parseDurationToMinutes("0.5d")    → 240
 *   parseDurationToMinutes("2w 1d")   → 2 * 5 * 8 * 60 + 1 * 8 * 60 = 5280
 *   parseDurationToMinutes("45")      → 45  (bare numbers interpreted as minutes)
 */
export interface DurationOptions {
  hoursPerDay?: number;
  daysPerWeek?: number;
}

const TOKEN_REGEX = /^(\d+(?:\.\d+)?)([wdhm])$/i;

export function parseDurationToMinutes(input: string, options: DurationOptions = {}): number {
  const hoursPerDay = options.hoursPerDay ?? 8;
  const daysPerWeek = options.daysPerWeek ?? 5;

  if (typeof input !== 'string') {
    throw new Error('INVALID_DURATION');
  }

  const raw = input.trim();
  if (!raw) throw new Error('INVALID_DURATION');

  // Allow a bare number (treated as minutes).
  if (/^\d+(?:\.\d+)?$/.test(raw)) {
    const minutes = Math.round(Number(raw));
    if (minutes <= 0) throw new Error('INVALID_DURATION');
    return minutes;
  }

  const tokens = raw.split(/\s+/);
  let totalMinutes = 0;
  for (const token of tokens) {
    const match = TOKEN_REGEX.exec(token);
    if (!match) {
      throw new Error('INVALID_DURATION');
    }
    const [, valueStr, unit] = match;
    const value = Number(valueStr);
    if (!Number.isFinite(value) || value < 0) {
      throw new Error('INVALID_DURATION');
    }
    switch ((unit as string).toLowerCase()) {
      case 'm':
        totalMinutes += value;
        break;
      case 'h':
        totalMinutes += value * 60;
        break;
      case 'd':
        totalMinutes += value * hoursPerDay * 60;
        break;
      case 'w':
        totalMinutes += value * daysPerWeek * hoursPerDay * 60;
        break;
      default:
        throw new Error('INVALID_DURATION');
    }
  }

  const rounded = Math.round(totalMinutes);
  if (rounded <= 0) throw new Error('INVALID_DURATION');
  return rounded;
}

export function formatMinutes(minutes: number, options: DurationOptions = {}): string {
  const hoursPerDay = options.hoursPerDay ?? 8;
  const daysPerWeek = options.daysPerWeek ?? 5;

  if (minutes <= 0) return '0m';
  const minutesPerDay = hoursPerDay * 60;
  const minutesPerWeek = daysPerWeek * minutesPerDay;

  const weeks = Math.floor(minutes / minutesPerWeek);
  const weekRemainder = minutes - weeks * minutesPerWeek;
  const days = Math.floor(weekRemainder / minutesPerDay);
  const dayRemainder = weekRemainder - days * minutesPerDay;
  const hours = Math.floor(dayRemainder / 60);
  const mins = dayRemainder - hours * 60;

  const parts: string[] = [];
  if (weeks) parts.push(`${weeks}w`);
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins) parts.push(`${mins}m`);
  return parts.join(' ');
}
